import React, { useRef, useState, useContext, useCallback } from "react";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/20/solid";
import { useDrag, useDrop } from "react-dnd";
import { firebaseAuth } from "../../../providers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DeleteIcon, InfoIcon } from "../../../Constants";
import { isAdditionalFieldNameSyntaxValid } from "../../../utils/additionalFieldEditor";
import { isCommerceItemAvailabilityHidden } from "../../../utils/ticketConfiguration";
import { CurrentEventDataContext } from "../CurrentEventDataContext";

const commerceVisibilityRuleNames = [
  "commerceItemPrerequisites",
  "commerceItemHidePrerequisites",
];

const updateCommerceVisibilityReferences = (field, previousName, nextName) =>
  commerceVisibilityRuleNames.reduce((updatedField, ruleName) => {
    if (!Array.isArray(updatedField[ruleName])) return updatedField;

    return {
      ...updatedField,
      [ruleName]: updatedField[ruleName]
        .map((fieldName) =>
          fieldName === previousName ? nextName : fieldName,
        )
        .filter(Boolean),
    };
  }, field);

const removeCommerceVisibilityReference = (field, fieldNameToRemove) =>
  commerceVisibilityRuleNames.reduce((updatedField, ruleName) => {
    if (!Array.isArray(updatedField[ruleName])) return updatedField;

    return {
      ...updatedField,
      [ruleName]: updatedField[ruleName].filter(
        (fieldName) => fieldName !== fieldNameToRemove,
      ),
    };
  }, field);

export const EventAdditionalField = ({
  id,
  item,
  index,
  isExpanded,
  moveField,
  onDuplicate,
  onRemove,
  onToggleExpanded,
  totalFields,
  updateData,
  eventData,
  setEventData,
}) => {
useContext(CurrentEventDataContext);
  useContext(firebaseAuth);
  const ref = useRef(null);
  const history = useNavigate();
    const fieldList = ['id', 'eventId', 'company', 'title', 'firstName', 'lastName', 'email', 'phone', 'address1', 'address2', 'city', 'state', 'postal', 'country', 'experience', 'purchaseTimeFrame', 'dealer', 'modelInterest', 'adyenResultCode', 'adyenRefusalReason', 'adyenRef', 'adyenRefDate', 'adyenLink', 'adyenLinkId', 'adyenLinkExpire', 'status', 'adyenPayemntStatus', 'hasWaiver', 'createdAt', 'updatedAt', 'leadSent'];
    const [help, setHelp] = useState({});
    const [isConditionalVisibilityExpanded, setIsConditionalVisibilityExpanded] =
      useState(false);
      const handleListChange = async (event, i, x, checkbox, v) => {
        let tmpList = [...(eventData[x] || [])];
        if (
          event.target.name === "fieldName" &&
          event.target.value &&
          !isAdditionalFieldNameSyntaxValid(event.target.value)
        ) {
          toast.error(
            "Field name can only contain letters, numbers, and underscores.",
          );
          return;
        }
        if (
          event.target.name === "fieldName" &&
          (fieldList.indexOf(event.target.value) >= 0 ||
            tmpList.some(
              (field, fieldIndex) =>
                fieldIndex !== i && field.fieldName === event.target.value,
            ))
        ) {
          toast.error('Invalid field name, field already in use.');
          return;
        }
        let val = v !== undefined ? v : (checkbox ? event.target.checked : event.target.value ? event.target.value : "");
        const previousField = tmpList[i] || {};
        const previousFieldName = previousField.fieldName;
        tmpList[i] = { ...previousField, [event.target.name]: val };

        if (
          event.target.name === "fieldType" &&
          val !== "CommerceItem"
        ) {
          delete tmpList[i].fieldCheckIn;
          delete tmpList[i].hideAvailableQuantity;
        }

        if (
          event.target.name === "fieldName" &&
          previousFieldName &&
          previousFieldName !== val
        ) {
          tmpList = tmpList.map((field) =>
            updateCommerceVisibilityReferences(
              field,
              previousFieldName,
              val,
            ),
          );
        }

        if (
          ((event.target.name === "fieldType" &&
            val !== "CommerceItem") ||
            (event.target.name === "fieldAdminOnly" && val === true)) &&
          previousField.fieldType === "CommerceItem" &&
          previousFieldName
        ) {
          tmpList = tmpList.map((field) =>
            removeCommerceVisibilityReference(field, previousFieldName),
          );
        }

        setEventData({ ...eventData, [x]: tmpList });
      };
      const handleCommerceVisibilityRuleChange = (
        ruleName,
        fieldName,
        checked,
      ) => {
        const additionalFields = [...(eventData?.additionalFields || [])];
        const currentField = additionalFields[index] || {};
        const currentRules = Array.isArray(currentField[ruleName])
          ? currentField[ruleName]
          : [];
        const oppositeRuleName =
          ruleName === "commerceItemPrerequisites"
            ? "commerceItemHidePrerequisites"
            : "commerceItemPrerequisites";
        const oppositeRules = Array.isArray(currentField[oppositeRuleName])
          ? currentField[oppositeRuleName]
          : [];

        additionalFields[index] = {
          ...currentField,
          [ruleName]: checked
            ? [...new Set([...currentRules, fieldName])]
            : currentRules.filter((itemName) => itemName !== fieldName),
          [oppositeRuleName]: checked
            ? oppositeRules.filter((itemName) => itemName !== fieldName)
            : oppositeRules,
        };

        setEventData({ ...eventData, additionalFields });
      };
        const handleChangeItem = useCallback((idkey, value) => {
          // console.log(idkey,value);
          // let tmpInputs = {...eventData};
          // tmpInputs[idkey] = value;
          // setEventData(tmpInputs);
          // // console.log(tmpInputs);
          // setEventData({...eventData,[idkey]:value});
          setEventData(value);
          // eslint-disable-next-line react-hooks/exhaustive-deps -- intent: stable callback identity; setEventData is a prop passed in from parent and assumed stable across renders
        }, []);
          const [{ handlerId }, drop] = useDrop({
            accept: "card",
            collect(monitor) {
              return {
                handlerId: monitor.getHandlerId(),
              };
            },
            hover(item, monitor) {
              if (!ref.current) {
                return;
              }
              const dragIndex = item.index;
              const hoverIndex = index;
              // Don't replace items with themselves
              if (dragIndex === hoverIndex) {
                return;
              }
              // Determine rectangle on screen
              const hoverBoundingRect = ref.current?.getBoundingClientRect();
              // Get vertical middle
              const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
              // Determine mouse position
              const clientOffset = monitor.getClientOffset();
              // Get pixels to the top
              const hoverClientY = clientOffset.y - hoverBoundingRect.top;
              // Only perform the move when the mouse has crossed half of the items height
              // When dragging downwards, only move when the cursor is below 50%
              // When dragging upwards, only move when the cursor is above 50%
              // Dragging downwards
              if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
              }
              // Dragging upwards
              if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
              }
              // Time to actually perform the action
              moveField(dragIndex, hoverIndex);
              // Note: we're mutating the monitor item here!
              // Generally it's better to avoid mutations,
              // but it's good here for the sake of performance
              // to avoid expensive index searches.
              item.index = hoverIndex;
            },
          });
          const [{ isDragging }, drag] = useDrag({
            type: "card",
            item: () => {
              console.log("dragging");
              console.log(id);
              console.log(index);
              return { id, index };
            },
            collect: (monitor) => ({
              isDragging: monitor.isDragging(),
            }),
          });
          const opacity = isDragging ? 0 : 1;
          drag(drop(ref));

  const labelCls = "block text-sm font-medium text-zinc-700 dark:text-zinc-300";
  const inputCls =
    "mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white";
  const checkboxCls =
    "h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white dark:focus:ring-white";
  const checkboxLabelCls = "inline-flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300";
  const helpTextCls = "mt-1 text-xs text-zinc-500 dark:text-zinc-400";
  const actionBtnCls =
    "inline-flex min-h-11 min-w-11 items-center justify-center rounded-md bg-zinc-50 px-2 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700";
  const fieldSummary = String(
    item?.fieldLabel ||
      item?.fieldName ||
      (item?.fieldType === "Textblock" ? item?.textCopy : item?.fieldType) ||
      "Blank field",
  )
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const panelId = `additional-field-panel-${index}`;
  const conditionalVisibilityPanelId = `additional-field-conditional-visibility-${index}`;
  const commerceVisibilityCandidates = (eventData?.additionalFields || [])
    .map((candidate, candidateIndex) => ({
      ...candidate,
      candidateIndex,
      displayLabel: String(candidate?.fieldLabel || candidate?.fieldName || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim(),
    }))
    .filter(
      (candidate) =>
        candidate.candidateIndex !== index &&
        candidate.fieldType === "CommerceItem" &&
        !candidate.fieldAdminOnly &&
        candidate.fieldName,
    );

  return (
              <div
                    ref={ref}
      style={{ opacity }}
      id={"card-" + index}
      data-handler-id={handlerId}
      className={`mb-3 cursor-move rounded-md border border-dashed border-zinc-400 bg-zinc-50 p-3 dark:border-zinc-600 dark:bg-zinc-900 ${index % 2 === 0 ? "alt-row cta-list-item" : "cta-list-item"}`}
              >
                <div
                  className="flex flex-nowrap items-center justify-between gap-3"
                  data-testid={`additional-field-header-${index}`}
                >
                  <div
                    className="min-w-0 flex-1"
                    data-testid={`additional-field-title-${index}`}
                  >
                    <h4 className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">
                      Additional Field #{index + 1} — {fieldSummary}
                    </h4>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {item?.fieldType || "Blank"}
                    </span>
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-2"
                    data-testid={`additional-field-controls-${index}`}
                  >
                    <button
                      aria-label={`Move additional field ${index + 1} up`}
                      className={actionBtnCls}
                      disabled={index === 0}
                      onClick={() => moveField(index, index - 1)}
                      title="Move field up"
                      type="button"
                    >
                      <ChevronUpIcon aria-hidden="true" className="size-5" />
                    </button>
                    <button
                      aria-label={`Move additional field ${index + 1} down`}
                      className={actionBtnCls}
                      disabled={index === totalFields - 1}
                      onClick={() => moveField(index, index + 1)}
                      title="Move field down"
                      type="button"
                    >
                      <ChevronDownIcon aria-hidden="true" className="size-5" />
                    </button>
                    <button
                      aria-label={`Duplicate additional field ${index + 1}`}
                      className={actionBtnCls}
                      onClick={() => onDuplicate(index)}
                      title="Duplicate field"
                      type="button"
                    >
                      <DocumentDuplicateIcon
                        aria-hidden="true"
                        className="size-5"
                        data-testid={`duplicate-additional-field-icon-${index}`}
                      />
                    </button>
                    <button
                      aria-controls={panelId}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} additional field ${index + 1}`}
                      className={actionBtnCls}
                      onClick={onToggleExpanded}
                      title={`${isExpanded ? "Collapse" : "Expand"} field`}
                      type="button"
                    >
                      {isExpanded ? (
                        <ArrowsPointingInIcon
                          aria-hidden="true"
                          className="size-5"
                        />
                      ) : (
                        <ArrowsPointingOutIcon
                          aria-hidden="true"
                          className="size-5"
                        />
                      )}
                    </button>
                  </div>
                </div>
                <div
                  className={isExpanded ? "" : "hidden"}
                  data-testid={`additional-field-panel-${index}`}
                  id={panelId}
                >
                <div className="mt-4">
                  <label className={labelCls}>Data Field Type</label>
                  <select
                    className={inputCls}
                    id={"fieldType" + index}
                    name="fieldType"
                    onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                    value={eventData?.additionalFields[index].fieldType || ''}
                  >
                    <option value="">Blank</option>
                    <option value="Textblock">Textblock</option>
                    <option value="Textbox">Textbox</option>
                    <option value="Textarea">Textarea</option>
                    <option value="Numberbox">Numberbox</option>
                    <option value="CommerceItem">Commerce Item</option>
                    {/* {arraySearch(eventData?.additionalFields,'CommerceItem',index) ? 
                      <option value="CommerceItem">Commerce Item</option>
                      :<></>
                    } */}
                    <option value="Dropdown">Dropdown</option>
                    <option value="Checkbox">Checkbox</option>
                    <option value="DateTime">Date and Time</option>
                    <option value="Date">Date</option>
                    <option value="Time">Time</option>
                  </select>
                </div>
                {eventData?.additionalFields[index].fieldType ? (<>
                  {eventData?.additionalFields[index].fieldType !== "Textblock" ? (<>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldLabel: !help.fieldLabel });
                        }}
                      >
                        Field Label #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldLabel ? helpTextCls : "hidden"}>
                        Text label for field #{index + 1}.
                      </div>
                      <input
                        type="text"
                        id={"fieldLabel" + index}
                        name="fieldLabel"
                        value={eventData?.additionalFields[index].fieldLabel || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldDescription: !help.fieldDescription });
                        }}
                      >
                        Field Description #{index + 1} {" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldDescription ? helpTextCls : "hidden"}>
                        Description for Field #{index + 1}.
                      </div>
                      <input
                        type="text"
                        id={"fieldDescription" + index}
                        name="fieldDescription"
                        value={eventData?.additionalFields[index].fieldDescription || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldName: !help.fieldName });
                        }}
                      >
                        Field Name #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div
                        className={help.fieldName ? helpTextCls : "hidden"}
                        id={`fieldNameHelp${index}`}
                      >
                        Required unique name for field #{index + 1}. Use only
                        letters, numbers, and underscores.
                      </div>
                      <input
                        aria-describedby={`fieldNameHelp${index}`}
                        type="text"
                        id={"fieldName" + index}
                        name="fieldName"
                        pattern="[A-Za-z0-9_]+"
                        required
                        title="Use only letters, numbers, and underscores."
                        value={eventData?.additionalFields[index].fieldName || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={labelCls}
                        htmlFor={`fieldCapacity${index}`}
                      >
                        Field Capacity Type #{index + 1}
                      </label>
                      <select
                        className={inputCls}
                        id={`fieldCapacity${index}`}
                        name="fieldCapacity"
                        onChange={(e) => {
                          handleListChange(e, index, "additionalFields");
                        }}
                        value={
                          eventData?.additionalFields[index].fieldCapacity ||
                          ""
                        }
                      >
                        <option value="">No Capacity</option>
                        <option value="eventCapacity">Event Capacity</option>
                        <option value="itemCapacity">Item Capacity</option>
                      </select>
                    </div>
                    {eventData?.additionalFields[index].fieldCapacity ===
                    "itemCapacity" ? (
                      <div className="mt-4">
                        <label
                          className={`${labelCls} cursor-help`}
                          htmlFor={`fieldCapacityNum${index}`}
                          onClick={() => {
                            setHelp({
                              ...help,
                              fieldCapacityNum: !help.fieldCapacityNum,
                            });
                          }}
                        >
                          Item Capacity #{index + 1}{" "}
                          <i className="ml-1 align-middle text-xs">
                            <InfoIcon />
                          </i>
                        </label>
                        <div
                          className={
                            help.fieldCapacityNum ? helpTextCls : "hidden"
                          }
                        >
                          Total number of this ticket that can be sold.
                        </div>
                        <input
                          className={inputCls}
                          id={`fieldCapacityNum${index}`}
                          min="0"
                          name="fieldCapacityNum"
                          onChange={(e) => {
                            handleListChange(e, index, "additionalFields");
                          }}
                          type="number"
                          value={
                            eventData?.additionalFields[index]
                              .fieldCapacityNum || ""
                          }
                        />
                      </div>
                    ) : null}
                  </>
                  ) : (<div></div>)}
                  {eventData?.additionalFields[index].fieldType === "Textblock" ? (
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, textCopy: !help.textCopy });
                        }}
                      >
                        Text Copy #{index + 1} {" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.textCopy ? helpTextCls : "hidden"}>
                        A block of text inline in the form.
                      </div>
                      <textarea
                        id={"textCopy" + index}
                        name="textCopy"
                        value={eventData?.additionalFields[index].textCopy || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      ></textarea>
                    </div>
                  ) : (<div></div>)}
                  {eventData?.additionalFields[index].fieldType === "Time" || eventData?.additionalFields[index].fieldType === "DateTime" || eventData?.additionalFields[index].fieldType === "Date" ? (<>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldMin: !help.fieldMin });
                        }}
                      >
                        Min #{index + 1} {" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.name ? helpTextCls : "hidden"}>
                        Min for Field #{index + 1} (semi-colon separated list).
                      </div>
                      <input
                        type={eventData?.additionalFields[index].fieldType === "Date" ? "date" : eventData?.additionalFields[index].fieldType === "Time" ? "time" : "datetime-local"}
                        id={"fieldMin" + index}
                        name="fieldMin"
                        value={eventData?.additionalFields[index].fieldMin || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldMax: !help.fieldMax });
                        }}
                      >
                        Max #{index + 1} {" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.name ? helpTextCls : "hidden"}>
                        Max for Field #{index + 1} (semi-colon separated list).
                      </div>
                      <input
                        type={eventData?.additionalFields[index].fieldType === "Date" ? "date" : eventData?.additionalFields[index].fieldType === "Time" ? "time" : "datetime-local"}
                        id={"fieldMax" + index}
                        name="fieldMax"
                        value={eventData?.additionalFields[index].fieldMax || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                  </>
                  ) : (<div></div>)}
                  {eventData?.additionalFields[index].fieldType === "Dropdown" ? (<>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldOptions: !help.fieldOptions });
                        }}
                      >
                        Field Options #{index + 1} {" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldOptions ? helpTextCls : "hidden"}>
                        Options for Field #{index + 1} (semi-colon separated list).
                      </div>
                      <input
                        type="text"
                        id={"fieldOptions" + index}
                        name="fieldOptions"
                        value={eventData?.additionalFields[index].fieldOptions || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                  </>
                  ) : (<div></div>)}
                  {eventData?.additionalFields[index].fieldType === "CommerceItem" ? (<>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldMin: !help.fieldMin });
                        }}
                      >
                        Field Min #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldMin ? helpTextCls : "hidden"}>
                        Min value for number field #{index + 1}.
                      </div>
                      <input
                        type="number"
                        id={"fieldMin" + index}
                        name="fieldMin"
                        value={eventData?.additionalFields[index].fieldMin || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldMax: !help.fieldMax });
                        }}
                      >
                        Field Max #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldMax ? helpTextCls : "hidden"}>
                        Max value for number field #{index + 1}.
                      </div>
                      <input
                        type="number"
                        id={"fieldMax" + index}
                        name="fieldMax"
                        value={eventData?.additionalFields[index].fieldMax || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldStep: !help.fieldStep });
                        }}
                      >
                        Field Cost #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldStep ? helpTextCls : "hidden"}>
                        Cost value for item #{index + 1}.
                      </div>
                      <input
                        type="number"
                        id={"fieldCost" + index}
                        name="fieldCost"
                        step="any"
                        value={eventData?.additionalFields[index].fieldCost || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label className={checkboxLabelCls}>
                        <input
                          type="checkbox"
                          onChange={(e) => { handleListChange(e, index, 'additionalFields', true) }}
                          id={"fieldCheckIn" + index}
                          name="fieldCheckIn"
                          checked={eventData?.additionalFields[index].fieldCheckIn || false}
                          className={checkboxCls}
                        />
                        <span>Allow individual item check-in</span>
                      </label>
                      <p className={helpTextCls}>
                        Staff can check in each purchased unit separately from the attendee and guest.
                      </p>
                    </div>
                    <div className="mt-4">
                      <label className={checkboxLabelCls}>
                        <input
                          type="checkbox"
                          onChange={(e) => { handleListChange(e, index, 'additionalFields', true) }}
                          id={"hideAvailableQuantity" + index}
                          name="hideAvailableQuantity"
                          checked={isCommerceItemAvailabilityHidden(
                            eventData?.additionalFields[index],
                          )}
                          className={checkboxCls}
                        />
                        <span>Hide available quantity on registration</span>
                      </label>
                      <p className={helpTextCls}>
                        On by default. Uncheck to show how many remain next to this item. Sold Out still appears when none are left.
                      </p>
                    </div>
                  </>
                  ) : (<div></div>)}
                  {eventData?.additionalFields[index].fieldType === "Numberbox" ? (<>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldMin: !help.fieldMin });
                        }}
                      >
                        Field Min #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldMin ? helpTextCls : "hidden"}>
                        Min value for number field #{index + 1}.
                      </div>
                      <input
                        type="number"
                        id={"fieldMin" + index}
                        name="fieldMin"
                        value={eventData?.additionalFields[index].fieldMin || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldMax: !help.fieldMax });
                        }}
                      >
                        Field Max #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldMax ? helpTextCls : "hidden"}>
                        Max value for number field #{index + 1}.
                      </div>
                      <input
                        type="number"
                        id={"fieldMax" + index}
                        name="fieldMax"
                        value={eventData?.additionalFields[index].fieldMax || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                    <div className="mt-4">
                      <label
                        className={`${labelCls} cursor-help`}
                        onClick={() => {
                          setHelp({ ...help, fieldStep: !help.fieldStep });
                        }}
                      >
                        Field Step #{index + 1}{" "}
                        <i className="ml-1 align-middle text-xs">
                          <InfoIcon />
                        </i>
                      </label>
                      <div className={help.fieldStep ? helpTextCls : "hidden"}>
                        Step value for number field #{index + 1}.
                      </div>
                      <input
                        type="number"
                        id={"fieldStep" + index}
                        name="fieldStep"
                        value={eventData?.additionalFields[index].fieldStep || ''}
                        onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                        className={inputCls}
                      />
                    </div>
                  </>
                  ) : (<div></div>)}
                  {eventData?.additionalFields[index].fieldType ? (
                    <fieldset className="mt-4 rounded-md border border-zinc-200 p-3 dark:border-white/10">
                      <legend className="w-full">
                        <span className="flex items-center justify-between gap-3">
                          <span className={labelCls}>
                            Conditional Visibility #{index + 1}
                          </span>
                          <button
                            aria-controls={conditionalVisibilityPanelId}
                            aria-expanded={isConditionalVisibilityExpanded}
                            aria-label={`${isConditionalVisibilityExpanded ? "Collapse" : "Expand"} conditional visibility for additional field ${index + 1}`}
                            className={actionBtnCls}
                            onClick={() =>
                              setIsConditionalVisibilityExpanded(
                                !isConditionalVisibilityExpanded,
                              )
                            }
                            title={`${isConditionalVisibilityExpanded ? "Collapse" : "Expand"} conditional visibility`}
                            type="button"
                          >
                            {isConditionalVisibilityExpanded ? (
                              <ArrowsPointingInIcon
                                aria-hidden="true"
                                className="size-5"
                              />
                            ) : (
                              <ArrowsPointingOutIcon
                                aria-hidden="true"
                                className="size-5"
                              />
                            )}
                          </button>
                        </span>
                      </legend>
                      <div
                        className={
                          isConditionalVisibilityExpanded ? "" : "hidden"
                        }
                        data-testid={`conditional-visibility-panel-${index}`}
                        id={conditionalVisibilityPanelId}
                      >
                        {commerceVisibilityCandidates.length ? (
                          <>
                          <div className="mt-2">
                            <p className={labelCls}>
                              Show only when any of these items is selected
                            </p>
                            <p className={helpTextCls}>
                              Leave all unchecked to show this field by default.
                            </p>
                            <div className="mt-2 grid gap-2">
                              {commerceVisibilityCandidates.map((candidate) => (
                                <label
                                  className={checkboxLabelCls}
                                  key={`show-${candidate.fieldName}-${candidate.candidateIndex}`}
                                >
                                  <input
                                    aria-label={`Show additional field ${index + 1} when ${candidate.displayLabel} is selected`}
                                    checked={
                                      eventData.additionalFields[
                                        index
                                      ].commerceItemPrerequisites?.includes(
                                        candidate.fieldName,
                                      ) || false
                                    }
                                    className={checkboxCls}
                                    onChange={(e) =>
                                      handleCommerceVisibilityRuleChange(
                                        "commerceItemPrerequisites",
                                        candidate.fieldName,
                                        e.target.checked,
                                      )
                                    }
                                    type="checkbox"
                                  />
                                  <span>
                                    {candidate.displayLabel}
                                    {candidate.displayLabel !== candidate.fieldName
                                      ? ` (${candidate.fieldName})`
                                      : ""}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="mt-4 border-t border-zinc-200 pt-4 dark:border-white/10">
                            <p className={labelCls}>
                              Hide when any of these items is selected
                            </p>
                            <div className="mt-2 grid gap-2">
                              {commerceVisibilityCandidates.map((candidate) => (
                                <label
                                  className={checkboxLabelCls}
                                  key={`hide-${candidate.fieldName}-${candidate.candidateIndex}`}
                                >
                                  <input
                                    aria-label={`Hide additional field ${index + 1} when ${candidate.displayLabel} is selected`}
                                    checked={
                                      eventData.additionalFields[
                                        index
                                      ].commerceItemHidePrerequisites?.includes(
                                        candidate.fieldName,
                                      ) || false
                                    }
                                    className={checkboxCls}
                                    onChange={(e) =>
                                      handleCommerceVisibilityRuleChange(
                                        "commerceItemHidePrerequisites",
                                        candidate.fieldName,
                                        e.target.checked,
                                      )
                                    }
                                    type="checkbox"
                                  />
                                  <span>
                                    {candidate.displayLabel}
                                    {candidate.displayLabel !== candidate.fieldName
                                      ? ` (${candidate.fieldName})`
                                      : ""}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <p className={`${helpTextCls} mt-3`}>
                            Hide rules take precedence. Choosing an item in one
                            list removes it from the other list.
                          </p>
                          </>
                        ) : (
                          <span className={helpTextCls}>
                            Add and name a public commerce item to configure
                            conditional visibility.
                          </span>
                        )}
                      </div>
                    </fieldset>
                  ) : null}
                  {eventData?.additionalFields[index].fieldType !== "Textblock" ? (<>
                    <div className="mt-4">
                      <label className={checkboxLabelCls}>
                        <input
                          type="checkbox"
                          onChange={(e) => { handleListChange(e, index, 'additionalFields', true) }}
                          id={"required" + index}
                          name="required"
                          checked={eventData?.additionalFields[index].required || false}
                          className={checkboxCls}
                        />
                        <span>Field Required</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <label className={checkboxLabelCls}>
                        <input
                          type="checkbox"
                          onChange={(e) => { handleListChange(e, index, 'additionalFields', true) }}
                          id={"fieldFullRow" + index}
                          name="fieldFullRow"
                          checked={eventData?.additionalFields[index].fieldFullRow || false}
                          className={checkboxCls}
                        />
                        <span>Field Full Row</span>
                      </label>
                    </div>
                    {!eventData?.additionalFields[index].fieldFullRow &&
                      <>
                        <div className="mt-4">
                          <label className={labelCls}>Field Number of Columns</label>
                          <select
                            className={inputCls}
                            id={"fieldColumns" + index}
                            name="fieldColumns"
                            onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                            value={eventData?.additionalFields[index].fieldColumns || ''}
                          >
                            <option value="12">1</option>
                            <option value="6">2</option>
                            <option value="4">3</option>
                            <option value="3">4</option>
                          </select>
                        </div>
                        <div className="mt-4">
                          <label className={labelCls}>Field Number of Columns Mobile</label>
                          <select
                            className={inputCls}
                            id={"fieldColumnsMobile" + index}
                            name="fieldColumnsMobile"
                            onChange={(e) => { handleListChange(e, index, 'additionalFields') }}
                            value={eventData?.additionalFields[index].fieldColumnsMobile || ''}
                          >
                            <option value="12">1</option>
                            <option value="6">2</option>
                            <option value="4">3</option>
                            <option value="3">4</option>
                          </select>
                        </div>
                      </>
                    }

                  </>
                  ) : (<div></div>)}
                </>
                ) : (<div></div>)}
                                    <div className="mt-4">
                      <label className={checkboxLabelCls}>
                        <input
                          type="checkbox"
                          onChange={(e) => { handleListChange(e, index, 'additionalFields', true) }}
                          id={"fieldAdminOnly" + index}
                          name="fieldAdminOnly"
                          checked={eventData?.additionalFields[index].fieldAdminOnly || false}
                          className={checkboxCls}
                        />
                        <span>Field Manage Only</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <label className={checkboxLabelCls}>
                        <input
                          type="checkbox"
                          onChange={(e) => { handleListChange(e, index, 'additionalFields', true) }}
                          id={"fieldHideOnWaitlist" + index}
                          name="fieldHideOnWaitlist"
                          checked={eventData?.additionalFields[index].fieldHideOnWaitlist || false}
                          className={checkboxCls}
                        />
                        <span>Hide on waitlist registration</span>
                      </label>
                    </div>
                <div className="mt-4 flex justify-end">
                  <button
                    aria-label={`Delete additional field ${index + 1}`}
                    className={`${actionBtnCls} text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400`}
                    onClick={() => onRemove(index)}
                    title="Delete field"
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className="block size-5"
                      data-testid={`delete-additional-field-icon-${index}`}
                    >
                      <DeleteIcon />
                    </span>
                  </button>
                </div>
                </div>
              </div>)};
